/**
 * Pure helpers for personalized RSVP invite links (Get Link modal, Firestore rsvpLinks).
 */

export const INVITE_LINK_QUERY_PARAM = 'inviteLink';

export interface PersonalInviteLinkRow {
    id: string;
    ticketIds: string[];
    link: string;
    shortUrl?: string;
    clickCount: number;
    createdAt: Date;
    inviterPaysFor: boolean;
}

export interface ResolvedInviteLinkParams {
    inviteLinkId: string;
    invitedBy: string;
    inviterPaysFor: boolean;
    ticketIds: string[];
}

/**
 * Clipboard/display: ma-to short URLs must never include query params (redirect target holds personalization).
 */
export function stripMaToShortUrlQuery(url: string): string {
    const t = url.trim();
    if (!t || !t.includes('ma-to.co.uk')) {
        return t;
    }
    try {
        const u = new URL(t);
        u.search = '';
        return u.toString();
    } catch (_error) {
        const q = t.indexOf('?');
        return q === -1 ? t : t.slice(0, q);
    }
}

/**
 * Append inviteLink (and optional invitedBy for ticket scope) to a base RSVP URL.
 * Payment terms are stored on the rsvpLinks document, not in query params.
 */
/** Canonical persisted URLs for a new rsvpLinks document (client fallback path). */
export function buildPersistedRsvpLinkUrls(
    baseUrl: string,
    linkId: string,
    invitedBy?: string,
): { link: string; targetUrl: string; shortUrl: string } {
    const targetUrl = appendInviteLinkToUrl(baseUrl, linkId, invitedBy);
    return { link: targetUrl, targetUrl, shortUrl: targetUrl };
}

export function appendInviteLinkToUrl(
    baseUrl: string,
    linkId: string,
    invitedBy?: string,
): string {
    const trimmedLinkId = linkId.trim();
    if (!trimmedLinkId) {
        return baseUrl;
    }

    const inviter = typeof invitedBy === 'string' ? invitedBy.trim() : '';

    try {
        const parsed = new URL(baseUrl);
        parsed.searchParams.set(INVITE_LINK_QUERY_PARAM, trimmedLinkId);
        parsed.searchParams.delete('inviterPaysFor');
        if (inviter) {
            parsed.searchParams.set('invitedBy', inviter);
        } else {
            parsed.searchParams.delete('invitedBy');
        }
        return parsed.toString();
    } catch (_error) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        const invitedByPart = inviter ? `&invitedBy=${encodeURIComponent(inviter)}` : '';
        return `${baseUrl}${separator}${INVITE_LINK_QUERY_PARAM}=${encodeURIComponent(trimmedLinkId)}${invitedByPart}`;
    }
}

/**
 * Href for copy/display after loading from Firestore or right after createShortRsvpLink.
 * Clean ma-to short URLs rely on the redirect target registered at creation (no query on the short URL).
 */
export function resolveStoredInviteLinkHref(
    data: { shortUrl?: unknown; link?: unknown; targetUrl?: unknown },
    linkId: string,
    invitedBy?: string,
): string {
    const shortRaw = String(data.shortUrl ?? '').trim();
    if (shortRaw.includes('ma-to.co.uk')) {
        return stripMaToShortUrlQuery(shortRaw);
    }
    if (shortRaw.includes(`${INVITE_LINK_QUERY_PARAM}=`)) {
        return shortRaw;
    }

    const base = String(data.shortUrl || data.link || data.targetUrl || '').trim();
    if (base.includes('ma-to.co.uk')) {
        return stripMaToShortUrlQuery(base);
    }
    if (base.includes(`${INVITE_LINK_QUERY_PARAM}=`)) {
        return base;
    }
    return appendInviteLinkToUrl(base, linkId, invitedBy);
}

/**
 * Whether an rsvpLinks document belongs to the current "My RSVP" dashboard (this RSVP only).
 * Event-wide links from the unit event dashboard omit inviterRsvpId and must not match here,
 * even when createdBy is the same user as the attendee.
 */
export function rsvpLinkBelongsToDashboard(
    data: Record<string, unknown>,
    context: { authUid: string | null | undefined; rsvpId: string | null | undefined },
): boolean {
    const { rsvpId } = context;
    if (!rsvpId || String(rsvpId).trim() === '') {
        return false;
    }
    const inviter = data.inviterRsvpId;
    if (inviter == null || String(inviter).trim() === '') {
        return false;
    }
    return String(inviter) === String(rsvpId);
}

/**
 * True when this rsvpLinks document is a personal invite link from the My RSVP dashboard
 * (createShortRsvpLink with inviterRsvpId). Admin RSVP Links omit inviterRsvpId.
 */
export function isPersonalInviteRsvpLinkDoc(data: Record<string, unknown>): boolean {
    const v = data.inviterRsvpId;
    return v != null && String(v).trim() !== '';
}

/**
 * True when this rsvpLinks document should appear in the event dashboard RSVP Links modal.
 * The discriminator is `inviterRsvpId`: personal invite links (My RSVP dashboard) always
 * set it, while event-wide links never do. Legacy event links may still carry `createdBy`
 * from older callable versions, so we intentionally ignore that field here.
 */
export function isEventDashboardRsvpLinkDoc(data: Record<string, unknown>): boolean {
    return !isPersonalInviteRsvpLinkDoc(data);
}

/**
 * Persisted payment option on link docs; legacy docs without the field default to inviter pays.
 */
export function inviterPaysForFromRsvpLinkDoc(data: Record<string, unknown>): boolean {
    return typeof data.inviterPaysFor === 'boolean' ? data.inviterPaysFor : true;
}

export function invitedByFromRsvpLinkDoc(data: Record<string, unknown>): string {
    const v = data.invitedBy;
    return typeof v === 'string' ? v.trim() : '';
}

export function resolveInviteParamsFromLinkDoc(
    linkId: string,
    data: Record<string, unknown>,
): ResolvedInviteLinkParams | null {
    const trimmedId = linkId.trim();
    if (!trimmedId) {
        return null;
    }

    return {
        inviteLinkId: trimmedId,
        invitedBy: invitedByFromRsvpLinkDoc(data),
        inviterPaysFor: inviterPaysForFromRsvpLinkDoc(data),
        ticketIds: Array.isArray(data.ticketIds)
            ? data.ticketIds.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
            : [],
    };
}

/**
 * Merge server-loaded rows with in-memory rows: same id → server wins; keep optimistic ids not yet on server.
 * Result sorted by createdAt descending.
 */
export function mergePersonalInviteLinkRows(
    prev: PersonalInviteLinkRow[],
    server: PersonalInviteLinkRow[],
): PersonalInviteLinkRow[] {
    const byId = new Map<string, PersonalInviteLinkRow>();
    for (const p of prev) {
        byId.set(p.id, p);
    }
    for (const l of server) {
        byId.set(l.id, l);
    }
    return Array.from(byId.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
